/**
 * Saved Quote Templates — the library behind the Convert-time template picker.
 *
 * A saved template is a REAL quote's full content (scope, materials, hours,
 * notes) stored under a name. This page lists them with their usage history —
 * which clients they've produced quotes for, and the Jobber quote #s / job #s
 * those turned into — searchable across all of it. Rename, edit the
 * description, archive / unarchive here.
 *
 * Saving happens on the quote itself (QuoteDraftReview → "💾 Save as Template");
 * picking happens on the Smart Assessment Convert button. All data flows
 * through /api/quote-brain (listQuoteTemplates / updateQuoteTemplate) — this
 * page never queries Supabase directly.
 *
 * Distinct from the playbook decision-rule "Templates" tab (QuoteTemplates.jsx)
 * and from the proposal-framing templates in the ReviewPush modal.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/apiFetch";

// Color tokens (match QuoteDraftReview)
const C = {
  bg:        "#0d0f12",
  surface:   "#161a20",
  surface2:  "#1e2328",
  border:    "#2a3140",
  accent:    "#a5ce3a",
  text:      "#e8eaf0",
  muted:     "#8892a4",
  warn:      "#f59e0b",
  good:      "#10b981",
};

const cardStyle = {
  background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16,
};
const btn = (variant) => ({
  background: variant === "primary" ? C.accent : "transparent",
  border: variant === "primary" ? "none" : `1px solid ${C.border}`,
  borderRadius: 8, color: variant === "primary" ? C.bg : C.text,
  fontWeight: 700, fontSize: 13, padding: "7px 14px", cursor: "pointer",
  fontFamily: "'Roboto', sans-serif",
});
const inputSt = {
  background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
  color: C.text, fontSize: 13, padding: "8px 10px", fontFamily: "'Roboto', sans-serif",
};
const pillSt = (color) => ({
  background: `${color}1f`, color, borderRadius: 999, fontSize: 11, fontWeight: 700,
  padding: "2px 9px", display: "inline-block",
});

const fmt$ = (n) => "$" + Number(n || 0).toLocaleString("en-CA", { maximumFractionDigits: 0 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-CA") : "");

export default function SavedQuoteTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null); // { id, name, description }

  const load = useCallback(async () => {
    try {
      const r = await apiFetch("/api/quote-brain", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "listQuoteTemplates", include_archived: true }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      setTemplates(j.templates || []);
      setError(null);
    } catch (e) { setError(e.message); setTemplates([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function update(id, patch) {
    setBusyId(id);
    try {
      const r = await apiFetch("/api/quote-brain", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "updateQuoteTemplate", id, ...patch }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      setEditing(null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  }

  const shown = useMemo(() => {
    let list = templates || [];
    if (!showArchived) list = list.filter((t) => !t.archived);
    const tokens = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((t) => {
        const hay = [t.name, t.description, t.project_title, t.service_type,
          ...(t.clients || []),
          ...(t.uses || []).flatMap((u) => [u.client_name, u.quote_num, u.job_num])]
          .filter(Boolean).join(" ").toLowerCase();
        return tokens.every((tok) => hay.includes(tok));
      });
    }
    return list;
  }, [templates, q, showArchived]);

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 14, background: C.surface2 }}>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
          Saved templates are real quotes you reuse: open any quote → <b style={{ color: C.text }}>💾 Save as Template</b>.
          When converting a Smart Assessment, the Convert button offers this list and adapts the pick to the new
          scope — client wording, cable runs, quantities — then re-prices from the pricebook.
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <input
          style={{ ...inputSt, flex: "1 1 280px" }}
          placeholder="Search name, client, job # or quote #…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <label style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Show archived
        </label>
      </div>

      {error && <div style={{ color: C.warn, fontSize: 13, marginBottom: 12 }}>⚠ {error}</div>}
      {templates === null && <div style={{ color: C.muted, padding: 24, textAlign: "center" }}>Loading…</div>}

      {!error && templates !== null && shown.length === 0 && (
        <div style={{ ...cardStyle, textAlign: "center", padding: 36, color: C.muted }}>
          {q ? `No templates match “${q}”.` : "No saved templates yet — open a quote you're proud of and hit “💾 Save as Template”."}
        </div>
      )}

      <div style={{ display: "grid", gap: 10 }}>
        {shown.map((t) => {
          const isEditing = editing?.id === t.id;
          const shape = t.section_count >= 2 ? `${t.section_count} sections`
            : t.option_count >= 2 ? `${t.option_count} options` : "single";
          return (
            <div key={t.id} style={{ ...cardStyle, opacity: t.archived ? 0.55 : 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  {isEditing ? (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                      <input
                        style={{ ...inputSt, flex: "1 1 200px" }}
                        value={editing.name}
                        autoFocus
                        onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
                      />
                      <input
                        style={{ ...inputSt, flex: "2 1 260px" }}
                        placeholder="Description"
                        value={editing.description}
                        onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))}
                      />
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
                        {t.name}
                        <span style={{ marginLeft: 10, fontSize: 13, fontWeight: 700, color: C.accent }}>{fmt$(t.grand_total)}</span>
                      </div>
                      {t.description && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{t.description}</div>}
                    </>
                  )}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                    {t.archived && <span style={pillSt(C.muted)}>archived</span>}
                    {t.service_type && <span style={pillSt("#7dd3fc")}>{String(t.service_type).replace(/_/g, " ")}</span>}
                    <span style={pillSt(C.muted)}>{shape}</span>
                    {t.labour_hours > 0 && <span style={pillSt(C.muted)}>{t.labour_hours}h</span>}
                    {t.material_count > 0 && <span style={pillSt(C.muted)}>{t.material_count} materials</span>}
                  </div>
                  {(t.uses || []).length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>
                        Used for
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {t.uses.map((u) => (
                          <button
                            key={u.id}
                            onClick={() => u.smart_quote_id && navigate(`/quote/${u.smart_quote_id}`)}
                            title={u.smart_quote_id ? "Open this quote" : undefined}
                            style={{
                              background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8,
                              color: C.text, fontSize: 11.5, padding: "5px 9px",
                              cursor: u.smart_quote_id ? "pointer" : "default",
                              fontFamily: "'Roboto', sans-serif", textAlign: "left",
                            }}
                          >
                            {[u.client_name || "(no client yet)",
                              u.quote_num ? `Q#${u.quote_num}` : null,
                              u.job_num ? `Job #${u.job_num}` : null,
                            ].filter(Boolean).join(" · ")}
                            {u.kind === "source" && <span style={{ color: C.muted }}> · original</span>}
                            <span style={{ color: C.muted }}> · {fmtDate(u.used_at)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {isEditing ? (
                    <>
                      <button
                        style={btn("primary")}
                        disabled={busyId === t.id}
                        onClick={() => update(t.id, { name: editing.name, description: editing.description })}
                      >
                        {busyId === t.id ? "Saving…" : "Save"}
                      </button>
                      <button style={btn("ghost")} disabled={busyId === t.id} onClick={() => setEditing(null)}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button
                        style={btn("ghost")}
                        disabled={busyId === t.id}
                        onClick={() => setEditing({ id: t.id, name: t.name, description: t.description || "" })}
                      >
                        Rename
                      </button>
                      <button
                        style={btn("ghost")}
                        disabled={busyId === t.id}
                        onClick={() => update(t.id, { archived: !t.archived })}
                        title={t.archived ? "Bring it back into the Convert picker" : "Hide it from the Convert picker (reversible)"}
                      >
                        {busyId === t.id ? "…" : t.archived ? "Unarchive" : "Archive"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
